import { useCallback, useEffect, useState } from 'react'
import { Ban, Loader2, Plus, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { createBlockout, deleteBlockout, fetchBlockouts } from '@/lib/api/blockouts'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import {
  BLOCKOUT_PRESETS,
  blockoutTargetLabel,
  blockoutTypeLabel,
  buildBlockoutPayload,
  formatBlockoutRange,
  validateBlockoutForm,
} from '@/lib/calendar/blockouts'
import { cn } from '@/lib/utils'

const EMPTY_FORM = {
  preset: 'lunch',
  blockoutType: /** @type {import('@/types/api').BlockoutType} */ ('stylist'),
  stylistId: '',
  chairId: '',
  title: '',
  date: '',
  startTime: '13:00',
  endTime: '14:00',
  allDay: false,
}

/**
 * @param {{
 *   range: { from: string, to: string }
 *   stylists: import('@/types/api').Stylist[]
 *   chairs: import('@/types/api').Chair[]
 *   defaultDate?: string
 * }} props
 */
export function BlockoutManager({ range, stylists, chairs, defaultDate }) {
  const [blockouts, setBlockouts] = useState([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    ...EMPTY_FORM,
    date: defaultDate ?? range.from,
  })

  const loadBlockouts = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const items = await fetchBlockouts(range)
      setBlockouts(items)
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load blockouts.'))
    } finally {
      setLoading(false)
    }
  }, [range])

  useEffect(() => {
    loadBlockouts()
  }, [loadBlockouts])

  useEffect(() => {
    setForm((current) => ({
      ...current,
      date: defaultDate ?? range.from,
    }))
  }, [defaultDate, range.from])

  const activeStylists = stylists.filter((stylist) => stylist.is_active)
  const activeChairs = chairs.filter((chair) => chair.is_active)

  async function handleSubmit(event) {
    event.preventDefault()

    const validationErrors = validateBlockoutForm(form)
    if (validationErrors.length > 0) {
      setError(validationErrors[0])
      return
    }

    setSubmitting(true)
    setError('')

    try {
      const created = await createBlockout(buildBlockoutPayload(form))
      setBlockouts((current) =>
        [...current, created].sort(
          (left, right) => new Date(left.starts_at).getTime() - new Date(right.starts_at).getTime(),
        ),
      )
      setShowForm(false)
      setForm({
        ...EMPTY_FORM,
        date: defaultDate ?? range.from,
      })
    } catch (err) {
      const fieldErrors = err?.response?.data?.errors
      if (fieldErrors) {
        setError(Object.values(fieldErrors).flat()[0] ?? 'Unable to create blockout.')
      } else {
        setError(apiErrorMessage(err, 'Unable to create blockout.'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * @param {import('@/types/api').Blockout} blockout
   */
  async function handleDelete(blockout) {
    if (blockout.is_system_managed) {
      return
    }

    setSubmitting(true)
    setError('')

    try {
      await deleteBlockout(blockout.id)
      setBlockouts((current) => current.filter((item) => item.id !== blockout.id))
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to remove blockout.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="card-surface p-4 lg:p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Ban className="h-4 w-4 text-primary" />
            Blockouts
          </p>
          <p className="text-[11px] text-muted-foreground">
            Lunch, training, holidays, and maintenance blocks for stylists, chairs, or the whole branch.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant={showForm ? 'secondary' : 'outline'}
          disabled={submitting}
          onClick={() => setShowForm((current) => !current)}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          {showForm ? 'Close' : 'Add blockout'}
        </Button>
      </div>

      {error ? (
        <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      ) : null}

      {showForm ? (
        <form
          onSubmit={handleSubmit}
          className="mb-4 grid gap-3 rounded-2xl border border-border/60 bg-muted/20 p-4 md:grid-cols-2 xl:grid-cols-4"
        >
          <label className="block md:col-span-2 xl:col-span-1">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Preset
            </span>
            <select
              value={form.preset}
              disabled={submitting}
              onChange={(event) => setForm((current) => ({ ...current, preset: event.target.value }))}
              className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
            >
              {BLOCKOUT_PRESETS.map((preset) => (
                <option key={preset.value} value={preset.value}>
                  {preset.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Scope
            </span>
            <select
              value={form.blockoutType}
              disabled={submitting}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  blockoutType: /** @type {import('@/types/api').BlockoutType} */ (event.target.value),
                }))
              }
              className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="stylist">Stylist</option>
              <option value="chair">Chair</option>
              <option value="branch">Whole branch</option>
            </select>
          </label>

          {form.blockoutType === 'stylist' ? (
            <label className="block">
              <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                Stylist
              </span>
              <select
                value={form.stylistId}
                disabled={submitting}
                onChange={(event) =>
                  setForm((current) => ({ ...current, stylistId: event.target.value }))
                }
                className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Select…</option>
                {activeStylists.map((stylist) => (
                  <option key={stylist.id} value={stylist.id}>
                    {stylist.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {form.blockoutType === 'chair' ? (
            <label className="block">
              <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                Chair
              </span>
              <select
                value={form.chairId}
                disabled={submitting}
                onChange={(event) => setForm((current) => ({ ...current, chairId: event.target.value }))}
                className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Select…</option>
                {activeChairs.map((chair) => (
                  <option key={chair.id} value={chair.id}>
                    {chair.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {form.preset === 'custom' ? (
            <label className="block md:col-span-2">
              <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                Title
              </span>
              <input
                type="text"
                disabled={submitting}
                value={form.title}
                onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                placeholder="e.g. Ramadan shortened hours"
                className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
              />
            </label>
          ) : null}

          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Date
            </span>
            <input
              type="date"
              disabled={submitting}
              value={form.date}
              onChange={(event) => setForm((current) => ({ ...current, date: event.target.value }))}
              className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
            />
          </label>

          <label className="flex items-end gap-2 md:col-span-2 xl:col-span-1">
            <input
              id="blockout-all-day"
              type="checkbox"
              disabled={submitting}
              checked={form.allDay}
              onChange={(event) => setForm((current) => ({ ...current, allDay: event.target.checked }))}
              className="h-4 w-4 rounded border-input"
            />
            <span className="pb-2 text-sm text-foreground">All day</span>
          </label>

          {!form.allDay ? (
            <>
              <label className="block">
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  Start
                </span>
                <input
                  type="time"
                  disabled={submitting}
                  value={form.startTime}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, startTime: event.target.value }))
                  }
                  className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  End
                </span>
                <input
                  type="time"
                  disabled={submitting}
                  value={form.endTime}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, endTime: event.target.value }))
                  }
                  className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                />
              </label>
            </>
          ) : null}

          <div className="flex items-end md:col-span-2 xl:col-span-4">
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save blockout'}
            </Button>
          </div>
        </form>
      ) : null}

      {loading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading blockouts…
        </div>
      ) : blockouts.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground">
          No blockouts in this range. Add lunch breaks, training, or branch closures.
        </p>
      ) : (
        <ul className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {blockouts.map((blockout) => (
            <li
              key={blockout.id}
              className={cn(
                'flex items-start justify-between gap-3 rounded-xl border border-border/60 bg-card px-3 py-2.5',
                blockout.is_system_managed && 'border-primary/20 bg-primary/[0.03]',
              )}
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-lg bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                    {blockoutTypeLabel(blockout.blockout_type)}
                  </span>
                  {blockout.is_system_managed ? (
                    <span className="rounded-lg bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                      Auto
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 truncate text-sm font-semibold text-foreground">{blockout.title}</p>
                <p className="truncate text-[11px] text-muted-foreground">
                  {blockoutTargetLabel(blockout)} · {formatBlockoutRange(blockout)}
                </p>
              </div>

              {!blockout.is_system_managed ? (
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => handleDelete(blockout)}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                  aria-label={`Remove ${blockout.title}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
